import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const EveryStudentWriting10MinutesWithoutGrading30MorePapers = () => {
  const post = getBlogPost(
    'every-student-writing-10-minutes-without-grading-30-more-papers',
  );
  return (
    <BlogArticle post={post}>
      <p>
        {
          "It's 7:42 a.m. on a Tuesday. You've got a stack of last week's writing samples on the corner of your desk, three meetings on your calendar, and a lesson that needs a bell-ringer in eighteen minutes. Somewhere in the back of your mind is a district priority you agree with completely: every student should be writing — every day, across every subject. And somewhere next to it is the math that keeps breaking the plan. Thirty students, four sections, one you."
        }
      </p>
      <p>
        {
          "If you've been teaching long enough, you know this tension isn't new. What's new is the pressure. Writing is back at the top of the instructional agenda in 2026, and this time it's everyone's job."
        }
      </p>
      <h2>{'The false choice most teachers are stuck inside'}</h2>
      <p>
        {
          'Here\'s how the conversation usually goes. "We need students writing more." Agreed. "Writing needs real feedback to improve." Also agreed — the research is unambiguous on this. John Hattie\'s synthesis of meta-analyses puts formative feedback at an effect size of 0.73, firmly in the "zone of desired effects." "So let\'s have students write more and give them feedback." And there\'s the trap.'
        }
      </p>
      <p>
        {
          'Because in most classrooms, "more writing" has historically meant "more grading." And "real feedback" has meant the teacher reading every paper, leaving margin notes, and staying up past 10 p.m. on a Sunday. The false choice sounds like this: either students write less than they need to, or you burn out. Either the feedback comes late, or it comes at a cost you can\'t keep paying.'
        }
      </p>
      <p>
        {'Most districts we work with are finally naming this honestly. '}
        <Link
          href="/c/blog-posts/beyond-the-ela-classroom-how-to-improve-writing-instruction-across-the-curriculum"
          passHref
        >
          <a>{"Writing instruction shouldn't live only in ELA"}</a>
        </Link>
        {
          " — science teachers, social studies teachers, even PE teachers can build in writing that deepens thinking. But that only works if the grading load doesn't multiply by subject."
        }
      </p>
      <h2>{'Why short, frequent writes work'}</h2>
      <p>
        {
          "There is a third path, and it's been in the research for decades. Shorter, more frequent writing — paired with timely feedback — outperforms longer, rarer assignments on almost every measure that matters. Students retain more. Engagement stays high. Struggling writers get more reps in a low-stakes container. Strong writers get pushed further because there's time to revise while the thinking is still warm."
        }
      </p>
      <p>
        {
          "The principle is simple: you don't need to grade every piece of writing for students to grow from it. You need students to produce writing often, get a useful signal quickly, and have a chance to act on that signal while it still matters."
        }
      </p>
      <p>
        {
          "In Curipod classrooms running this pattern, we've watched the shift happen in weeks, not semesters. One group of veteran teachers in Southern California saw "
        }
        <Link href="/c/blog-posts/46-percent-caasp-growth-ca" passHref>
          <a>{'a 46% jump in CAASPP writing scores'}</a>
        </Link>
        {
          ' after embedding short, rubric-aligned writes into their regular instruction. Not just as test prep. As teaching.'
        }
      </p>
      <h2>{'Three classroom-ready routines you can run tomorrow'}</h2>
      <p>
        {
          "You don't need a new curriculum to do this. You need three small moves that fit inside the lessons you already teach."
        }
      </p>
      <h3>{'1. The bell-ringer QuickWrite (3-5 minutes)'}</h3>
      <p>
        {
          'Open class with a one-question prompt tied to what you\'re about to teach. "What do you already think is true about photosynthesis?" "In one sentence: what makes a persuasive argument persuasive?" "Which of these two graphs tells a clearer story, and why?"'
        }
      </p>
      <p>
        {
          "Every student writes. Everyone — including the student who never raises a hand, the student who's still warming up, the multilingual learner who processes better on the page than out loud. They each get immediate feedback on their sentence before you've even taken attendance. You get a snapshot of the room's thinking before you teach a minute of content."
        }
      </p>
      <p>
        {'You are not grading this. You are reading the signal and adjusting the lesson.'}
      </p>
      <h3>{'2. The mid-lesson check-for-understanding write (5 minutes)'}</h3>
      <p>
        {
          "Twenty minutes into a lesson, stop talking. Ask students to explain, in their own words, the concept you just taught. Or to apply it to a new example. Or to argue for or against a claim you've put in front of them."
        }
      </p>
      <p>
        {
          'Again — every student writes. Students get real-time feedback against a simple criterion ("Does your answer use evidence from the passage?"). Then — and this is the move most teachers skip — bring the class back together. Share one strong response. Share one confusion. Re-teach the thing the data told you to re-teach. Let students revise.'
        }
      </p>
      <p>
        {
          "This is the loop the research calls \"dialogic writing instruction,\" and it's one of the highest-leverage things you can do in a classroom. Robin Alexander's work on classroom talk, Mercer and Resnick's research on discussion — it all converges on the same idea: writing and talking together, with feedback in the middle, is how thinking gets deeper."
        }
      </p>
      <h3>{'3. The exit-ticket revision (5-10 minutes)'}</h3>
      <p>
        {
          'End class with one prompt that asks students to do something with what they learned today. A paragraph. A claim with evidence. A one-sentence summary a fifth grader could understand.'
        }
      </p>
      <p>
        {
          "They write. They get feedback. They revise once — right there, in the last few minutes of class, while the learning is still fresh. You collect the data; you don't carry it home."
        }
      </p>
      <p>
        {
          "The exit-ticket revision is the single most underused move in K-12 writing instruction. And it is free. It is already a routine teachers know. All that's missing is the feedback loop that lets students try again in the same class period — which is exactly what makes the difference between a write-and-forget and a write-and-grow."
        }
      </p>
      <h2>{'What the teacher still owns'}</h2>
      <p>
        {
          'Let\'s be direct about this, because it\'s the part that matters most. AI feedback is not a replacement for you. It\'s a first pass. It catches the surface stuff — "did the student use evidence," "did they answer the question," "is this sentence on-topic" — so you can spend your minutes on the things only you can see.'
        }
      </p>
      <p>
        {
          "You still choose the prompt. You still write the rubric. You still read the room and decide what to re-teach. You still notice when a student who usually doesn't write has written a full paragraph, and you still celebrate it by name. You still decide which pieces get a full response from you — usually the ones that will become a summative assessment, or the ones where a student is on the edge of a breakthrough."
        }
      </p>
      <p>
        {
          "What you stop doing is reading 120 first drafts a week to check whether students followed the directions. That's not where your expertise moves the needle. Your expertise moves the needle when you're choosing what to teach next, responding to a student whose thinking just clicked, and coaching a writer who's finally ready to revise. AI handles the reps; you handle the coaching."
        }
      </p>
      <h2>{'Why this is the year to build the routine'}</h2>
      <p>
        {
          'Three things are converging in 2026 that make daily writing practice both easier and more urgent.'
        }
      </p>
      <p>
        {
          "First, the science-of-reading wave has reached writing. Districts are realizing that strong readers don't automatically become strong writers, and that writing is where comprehension gets stress-tested. If a student can't explain a concept in a paragraph, they don't fully understand it yet."
        }
      </p>
      <p>
        {'Second, '}
        <Link
          href="/c/blog-posts/from-test-prep-to-lasting-learning-how-to-raise-standardized-test-scores-without-burning-out-teachers-or-students"
          passHref
        >
          <a>{'state assessments are weighting writing more heavily'}</a>
        </Link>
        {
          ' — and administrators are looking for evidence that students are writing regularly, not just in the weeks before a test.'
        }
      </p>
      <p>
        {
          'Third, teacher burnout data keeps getting worse, and the pressure to add more without adding support is the single biggest driver. Anything that raises student writing output without raising teacher grading load is the rare intervention that serves both sides of the desk.'
        }
      </p>
      <p>
        {
          "You don't need a new curriculum to make this happen. You need a 10-minute routine, a teacher-controlled feedback loop, and the permission to stop grading every single thing students write. The rest is practice."
        }
      </p>
      <p>
        {
          'If you want to see what this looks like in a live classroom, try it with one lesson this week. Pick a bell-ringer. Run a QuickWrite. Watch what happens when every student writes — and every student gets feedback while the thinking is still fresh. '
        }
        <Link href="/c/blogs" passHref>
          <a>{"That's the shift"}</a>
        </Link>
        {'.'}
      </p>
      <h2>{'Sources'}</h2>
      <ul>
        <li>
          {'Hattie, J. (2009, updated 2023). '}
          <em>{'Visible Learning'}</em>
          {'. Feedback effect size: d = 0.73.'}
        </li>
        <li>
          {'Alexander, R. (2018). '}
          <em>{'Developing Dialogic Teaching: Genesis, Process, Trial'}</em>
          {'.'}
        </li>
        <li>
          {'Mercer, N., & Hodgkinson, S. (2008). '}
          <em>{'Exploring Talk in School'}</em>
          {'.'}
        </li>
        <li>
          {'Graham, S., & Perin, D. (2007). '}
          <em>
            {
              'Writing Next: Effective Strategies to Improve Writing of Adolescents in Middle and High Schools'
            }
          </em>
          {'. Carnegie Corporation.'}
        </li>
        <li>
          {'National Commission on Writing (2003, updated reports). '}
          <em>{'The Neglected "R": The Need for a Writing Revolution'}</em>
          {'.'}
        </li>
        <li>
          {
            'Curipod CAASPP case study (2024). Southern California cohort, 46% growth in writing scores.'
          }
        </li>
      </ul>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default EveryStudentWriting10MinutesWithoutGrading30MorePapers;
